import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Check, X, Minus } from 'lucide-react';

const rows = [
  { feature: 'Price point', vf: 'From $199/mo', nuance: '$500+/mo', freed: '$300+/mo' },
  { feature: 'Works offline / low bandwidth', vf: true, nuance: false, freed: false },
  { feature: 'Auto ICD-10 billing codes', vf: true, nuance: false, freed: 'partial' },
  { feature: 'Section 330 / grant-aligned', vf: true, nuance: false, freed: false },
  { feature: 'Multilingual support', vf: true, nuance: 'partial', freed: false },
  { feature: 'Built for rural clinics', vf: true, nuance: false, freed: false },
];

function CellValue({ value }) {
  if (value === true) return <Check size={18} strokeWidth={2.5} className="text-vf-forest mx-auto" />;
  if (value === false) return <X size={18} strokeWidth={2} className="text-vf-charcoal/30 mx-auto" />;
  if (value === 'partial') return <Minus size={18} strokeWidth={2} className="text-vf-charcoal/40 mx-auto" />;
  return <span className="font-body text-sm text-vf-charcoal/80">{value}</span>;
}

export default function ComparisonTable() {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section data-testid="comparison-table-section" className="py-24 md:py-32 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        <div ref={ref} className={`scroll-fade-up ${isVisible ? 'visible' : ''}`}>
          <p className="uppercase text-xs tracking-[0.2em] text-vf-forest font-body font-semibold mb-4 text-center">
            Comparison
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-vf-charcoal tracking-tight mb-4 text-center leading-tight">
            How we compare.
          </h2>
          <p className="font-body text-base md:text-lg text-vf-charcoal/60 text-center max-w-xl mx-auto mb-12">
            The only AI scribe purpose-built for rural healthcare.
          </p>
        </div>

        {/* Scroll hint on mobile */}
        <p className="md:hidden font-body text-xs text-vf-charcoal/40 text-center mb-3">
          Scroll horizontally to see full table
        </p>

        <div className="overflow-x-auto" data-testid="comparison-table">
          <table className="w-full min-w-[600px] border-collapse">
            <thead>
              <tr className="border-b-2 border-vf-forest">
                <th className="text-left font-body text-xs uppercase tracking-[0.15em] text-vf-charcoal/60 py-4 pr-4 w-[35%]">
                  Feature
                </th>
                <th className="text-center font-body text-xs uppercase tracking-[0.15em] py-4 px-4 w-[22%]">
                  <span className="text-vf-forest font-bold">VitalsFlow AI</span>
                </th>
                <th className="text-center font-body text-xs uppercase tracking-[0.15em] text-vf-charcoal/50 py-4 px-4 w-[22%]">
                  Nuance DAX
                </th>
                <th className="text-center font-body text-xs uppercase tracking-[0.15em] text-vf-charcoal/50 py-4 px-4 w-[21%]">
                  Freed AI
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.feature} className={`border-b border-vf-sage/60 ${i % 2 === 0 ? 'bg-white' : 'bg-vf-salt'}`}>
                  <td className="font-body text-sm text-vf-charcoal py-4 pr-4 font-medium">
                    {row.feature}
                  </td>
                  <td className="text-center py-4 px-4 bg-vf-mint/10">
                    <CellValue value={row.vf} />
                  </td>
                  <td className="text-center py-4 px-4">
                    <CellValue value={row.nuance} />
                  </td>
                  <td className="text-center py-4 px-4">
                    <CellValue value={row.freed} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
